/** Page coordination only. Pebble owns tab navigation, media and cart requests. */
class ChaiLanding extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.tabs = this.querySelector("tabs-component");
    this.sticky = this.querySelector("[data-chai-sticky]");
    this.stickyButton = this.querySelector("[data-chai-sticky-add]");
    this.stickyButtonText = this.stickyButton?.querySelector(
      ".chai-sticky__button-text",
    );
    this.stickySpinner = this.stickyButton?.querySelector(
      ".chai-sticky__spinner",
    );
    this.tabObserver = new MutationObserver(() => this.syncTab());
    this.tabObserver.observe(this.tabs, {
      attributes: true,
      attributeFilter: ["active-index"],
    });
    this.scheduleStickyVisibility = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateStickyVisibility();
      });
    };
    window.addEventListener("scroll", this.scheduleStickyVisibility, {
      passive: true,
      signal,
    });
    window.addEventListener("resize", this.scheduleStickyVisibility, {
      signal,
    });
    this.addEventListener("change", (event) => this.selectVariant(event), {
      signal,
    });
    this.stickyButton.addEventListener(
      "click",
      () => {
        this.setStickyLoading(true);
        this.primaryButton?.click();
      },
      { signal },
    );
    document.addEventListener("cart:update", () => this.setStickyLoading(false), {
      signal,
    });
    document.addEventListener("shopify:section:load", () => this.syncTab(), {
      signal,
    });
    customElements.whenDefined("tabs-component").then(() => {
      if (!this.isConnected) return;
      const tab = new URLSearchParams(location.search).get("tab");
      if (tab === "tab2") this.tabs.selectTab(1);
      this.syncTab();
    });
    this.syncTab();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.tabObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
  }

  syncTab() {
    const index = Number(this.tabs.getAttribute("active-index") || 0);
    const key = `tab${index + 1}`;
    document.querySelectorAll("[data-chai-tab]").forEach((section) => {
      section.hidden =
        section.dataset.chaiTab && section.dataset.chaiTab !== key;
      if (section.hidden) {
        section
          .querySelectorAll("deferred-media")
          .forEach((media) => media.pauseMedia?.());
      }
    });
    this.activePanel = this.querySelector(
      `[role="tabpanel"][data-index="${index}"]`,
    );
    this.primaryButton = this.activePanel?.querySelector(".chai-fighter__atc");
    this.scheduleStickyVisibility();
    this.syncSticky();
  }

  updateStickyVisibility() {
    const shouldHide =
      !this.primaryButton ||
      this.primaryButton.getBoundingClientRect().bottom > 0;
    this.sticky.hidden = shouldHide;
    this.sticky.classList.toggle("is-visible", !shouldHide);
    this.sticky.setAttribute("aria-hidden", String(shouldHide));
  }

  selectVariant(event) {
    const input = event.target.closest("[data-chai-variant-option]");
    if (!input) return;
    const panel = input.closest("[data-chai-fighter-panel]");
    const form = panel.querySelector("form");
    const button = form.querySelector('[name="add"]');
    const available = input.dataset.available === "true";
    form.elements.id.value = input.value;
    form.elements.quantity.value = input.dataset.minimum;
    panel.querySelector("[data-chai-price]").textContent = input.dataset.price;
    panel.querySelector("[data-chai-weight]").textContent = input.dataset.title;
    button.disabled = !available;
    button.querySelector(".add-to-cart-text__content").textContent = available
      ? "ADD TO CART"
      : "SOLD OUT";
    button.setAttribute("aria-label", available ? "ADD TO CART" : "SOLD OUT");
    const price = button.querySelector("[data-add-to-cart-price]");
    price.textContent = `— ${input.dataset.price}`;
    price.hidden = !available;
    panel
      .querySelectorAll(".chai-fighter__weight-label-input")
      .forEach((label) => {
        label.classList.toggle("is-selected", label.contains(input));
      });
    this.syncSticky();
  }

  syncSticky() {
    if (!this.primaryButton) return;
    const panel = this.activePanel;
    this.sticky.querySelector("[data-chai-sticky-title]").textContent =
      panel.querySelector(".chai-fighter__title").textContent;
    const price = panel.querySelector("[data-chai-price]").textContent;
    const weight = panel.querySelector("[data-chai-weight]")?.textContent;
    this.sticky.querySelector("[data-chai-sticky-price]").textContent =
      `${price}${weight ? ` | ${weight}` : ""}`;
    const button = this.sticky.querySelector("button");
    button.disabled = this.primaryButton.disabled;
    if (!this.stickyButton.classList.contains("btn--loading")) {
      this.stickyButtonText.textContent = button.disabled
        ? "SOLD OUT"
        : "ADD TO CART";
    }
    const source = panel.querySelector(".chai-fighter__image img");
    const image = this.sticky.querySelector("img");
    if (source) {
      const url = new URL(source.currentSrc || source.src);
      url.searchParams.set("width", "128");
      image.src = url.href;
      image.hidden = false;
    }
  }

  setStickyLoading(isLoading) {
    if (!this.stickyButton) return;
    this.stickyButton.classList.toggle("btn--loading", isLoading);
    this.stickyButton.disabled = isLoading || this.primaryButton?.disabled;
    this.stickySpinner?.classList.toggle("hidden", !isLoading);
    if (isLoading) this.stickyButtonText.textContent = "ADDING";
  }
}

if (!customElements.get("chai-landing")) {
  customElements.define("chai-landing", ChaiLanding);
}

/**
 * Coordinate one active video; Pebble still owns loading, playback and
 * controls for the underlying media elements.
 *
 * This element drives its own Swiper instance rather than Pebble's native
 * <carousel-slider> element (assets/carousel.js), because that shared,
 * sitewide component/bundle doesn't include the coverflow effect this
 * gallery needs. See src/scripts/chai-gallery-swiper.js for the dedicated,
 * section-scoped Swiper build this loads.
 */
class ChaiVideoGallery extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.media = [...this.querySelectorAll("deferred-media")];
    this.pauseAll = () => this.media.forEach((media) => media.pauseMedia?.());
    this.playActive = () => {
      const media =
        this.swiper?.slides[this.swiper.activeIndex]?.querySelector(
          "deferred-media",
        );
      if (
        !media ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }
      this.selectMedia(media);
      const bounds = media.getBoundingClientRect();
      if (
        !this.hidden &&
        !document.hidden &&
        bounds.top < window.innerHeight &&
        bounds.bottom > 0
      ) {
        media.playMedia?.();
      }
    };
    document.addEventListener(
      "media:started-playing",
      (event) => {
        if (this.media.includes(event.detail.resource)) {
          this.selectMedia(event.detail.resource);
        }
      },
      { signal },
    );
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.hidden) this.pauseAll();
      },
      { signal },
    );
    const initToken = (this.initToken = {});
    this.initSwiper(initToken);
  }

  async initSwiper(initToken) {
    const carousel = this.querySelector("[data-chai-gallery-carousel]");
    const container = carousel?.querySelector(".swiper");
    const configEl = this.querySelector("[data-chai-gallery-config]");
    const swiperSrc = this.dataset.swiperSrc;
    if (!carousel || !container || !swiperSrc) return;

    let options = {};
    try {
      options = configEl?.textContent ? JSON.parse(configEl.textContent) : {};
    } catch (_e) {
      console.error("ChaiVideoGallery: invalid JSON in gallery config");
    }

    const { Swiper, Navigation, EffectCoverflow } = await import(swiperSrc);
    // Bail if disconnected, or reconnected again (e.g. a theme-editor
    // section re-render), while the module was loading.
    if (!this.isConnected || this.initToken !== initToken) return;

    this.swiper = new Swiper(container, {
      ...options,
      modules: [Navigation, EffectCoverflow],
      navigation: {
        prevEl: this.querySelector('[ref="prev"]'),
        nextEl: this.querySelector('[ref="next"]'),
      },
      on: {
        slideChangeTransitionStart: () => this.pauseAll(),
        slideChangeTransitionEnd: () => this.playActive(),
      },
    });

    this.playActive();
  }

  selectMedia(selected) {
    this.media.forEach((media) => {
      media.toggleAttribute("data-autoplay-blocked", media !== selected);
      if (media !== selected) media.pauseMedia?.();
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.pauseAll?.();
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }
}

if (!customElements.get("chai-video-gallery")) {
  customElements.define("chai-video-gallery", ChaiVideoGallery);
}
